import {
	RiCheckDoubleLine,
	RiMailLine,
	RiMailOpenLine,
	RiNotification3Line,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { Button } from "@/components/ui/button";
import { deepLinkFor } from "@/lib/notification-link";

export const Route = createFileRoute("/_app/notifications")({
	component: NotificationsPage,
});

function NotificationsPage() {
	const queryClient = useQueryClient();

	const { data, isLoading } = $api.useQuery("get", "/api/v1/notifications", {
		params: { query: { limit: 50, offset: 0 } },
	});

	const { data: unreadData } = $api.useQuery("get", "/api/v1/notifications/unread-count");

	const notifications = data?.data ?? [];
	const unreadCount = unreadData?.count ?? 0;

	async function handleMarkRead(id: string) {
		const { error } = await fetchClient.POST("/api/v1/notifications/{id}/read", {
			params: { path: { id } },
		});
		if (error) {
			toast.error("Failed to mark as read");
			return;
		}
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/notifications"],
		});
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/notifications/unread-count"],
		});
	}

	async function handleMarkAllRead() {
		const { error } = await fetchClient.POST("/api/v1/notifications/read-all");
		if (error) {
			toast.error("Failed to mark all as read");
			return;
		}
		toast.info("All notifications marked as read");
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/notifications"],
		});
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/notifications/unread-count"],
		});
	}

	function timeAgo(dateStr: string) {
		const diff = Date.now() - new Date(dateStr).getTime();
		const mins = Math.floor(diff / 60000);
		if (mins < 1) return "just now";
		if (mins < 60) return `${mins}m ago`;
		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h ago`;
		const days = Math.floor(hrs / 24);
		return `${days}d ago`;
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
					{unreadCount > 0 && <p className="text-sm text-muted-foreground">{unreadCount} unread</p>}
				</div>
				{unreadCount > 0 && (
					<Button variant="outline" size="sm" onClick={handleMarkAllRead}>
						<RiCheckDoubleLine className="size-4 mr-1" />
						Mark all read
					</Button>
				)}
			</div>

			{isLoading ? (
				<p className="text-muted-foreground">Loading...</p>
			) : notifications.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<RiNotification3Line className="size-12 text-muted-foreground/30 mb-3" />
					<p className="text-muted-foreground">No notifications yet</p>
				</div>
			) : (
				<div className="space-y-1">
					{notifications.map((n) => (
						<div
							key={n.id}
							className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
								n.read ? "bg-background border-border" : "bg-accent/30 border-accent"
							}`}
						>
							<div className="mt-0.5">
								{n.read ? (
									<RiMailOpenLine className="size-4 text-muted-foreground" />
								) : (
									<RiMailLine className="size-4 text-primary" />
								)}
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									{(() => {
										const href = deepLinkFor(n);
										return href ? (
											<Link to={href} className="text-sm font-medium truncate hover:underline">
												{n.title}
											</Link>
										) : (
											<p className="text-sm font-medium truncate">{n.title}</p>
										);
									})()}
									<span className="text-xs text-muted-foreground whitespace-nowrap">
										{timeAgo(n.createdAt)}
									</span>
								</div>
								{n.body && (
									<p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
								)}
								{n.actorName && (
									<p className="text-xs text-muted-foreground mt-1">by {n.actorName}</p>
								)}
							</div>
							{!n.read && (
								<Button
									variant="ghost"
									size="sm"
									className="shrink-0"
									onClick={() => handleMarkRead(n.id)}
								>
									Mark read
								</Button>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
